// PDF del detalle de un cierre: encabezado + "Ventas: reportado por el cajero vs sistema" + Compras + Resumen de compras.
// Termina en el Resumen de compras: no incluye gastos, salidas, instrucciones, liquidación, historial ni comprobantes.
// Los valores salen de cuadreVista (la misma fuente que la pantalla): no hay cálculos propios del PDF.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toMoney } from '../utils/numbers';
import { formatDate, formatDateTime } from '../utils/dates';
import { nombreTurno } from '../utils/turnos';
import { textoResultado } from '../components/cierres/estadoCierre';
import { cuadreVista, notaFondo, resumirCompras, formaPagoCompra, cantidadItem, unitarioItem } from '../components/cierres/cuadreVista';

const MARCA = [31, 45, 51];    // encabezados de tabla (color del menú), igual que reporteTabla
const ACENTO = [47, 111, 115];
const GRIS = [91, 107, 115];
const SUAVE = [238, 244, 244];
const M = 40, PIE = 42;

// Las fuentes estándar del PDF no tienen el signo menos tipográfico (U+2212): se usa el guion normal.
const txt = (v) => String(v ?? '').replace(/−/g, '-');
const dinero = (v) => (v == null || v === '' ? 'Pendiente' : toMoney(v));
const conSigno = (v) => (v == null ? 'Pendiente' : `${v > 0 ? '+' : ''}${toMoney(v)}`);
const num = (content, estilos) => ({ content: txt(content), styles: { halign: 'right', ...estilos } });
const NEGRITA = { fontStyle: 'bold' };
const RESALTE = { fontStyle: 'bold', fillColor: SUAVE };

/** cierre-QueTaco1-2026-11-01-dia.pdf (sin tildes, espacios ni símbolos). */
export const nombreArchivoCierre = ({ sucursal, fecha, turnoId }) => {
  const limpio = String(sucursal || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '');
  const turno = String(turnoId || 'turno').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return `cierre-${limpio || 'sucursal'}-${fecha || 'sin-fecha'}-${turno}.pdf`;
};

/**
 * @param detalle    respuesta de getCierreDetalle ({ cierre, sucursal, compras, siguienteApertura })
 * @param servicios  servicios activos de la sucursal ({ pos, pedidosYa }), igual que en pantalla
 */
export function construirCierreDetallePdf(detalle, { servicios = {}, generado = new Date() } = {}) {
  const c = detalle.cierre, compras = detalle.compras || [];
  const v = cuadreVista(c, { servicios });
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const W = pdf.internal.pageSize.getWidth(), H = pdf.internal.pageSize.getHeight();
  let y = M;

  // Salto de página si el bloque que sigue no cabe (y no estamos ya al inicio de una página).
  const asegurar = (alto) => { if (y > M && y + alto > H - PIE) { pdf.addPage(); y = M; } };
  const titulo = (texto, alto = 60) => {
    asegurar(alto);
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12); pdf.setTextColor(...MARCA);
    pdf.text(txt(texto), M, y + 10); y += 18;
  };
  const nota = (texto, color = GRIS) => {
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(...color);
    const lineas = pdf.splitTextToSize(txt(texto), W - 2 * M);
    asegurar(lineas.length * 11);
    pdf.text(lineas, M, y + 6); y += lineas.length * 11 + 6;
  };
  const tabla = (opts) => {
    autoTable(pdf, { startY: y, margin: { left: M, right: M, top: M, bottom: PIE }, theme: 'grid',
      rowPageBreak: 'avoid', showHead: 'everyPage', showFoot: 'lastPage',
      styles: { font: 'helvetica', fontSize: 8.5, cellPadding: 4, textColor: [31, 41, 51], lineColor: [217, 226, 227], lineWidth: 0.5 },
      headStyles: { fillColor: MARCA, textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: SUAVE, textColor: MARCA, fontStyle: 'bold' }, ...opts });
    y = pdf.lastAutoTable.finalY + 12;
  };

  // 1. Encabezado compacto.
  pdf.setFillColor(...ACENTO); pdf.rect(0, 0, W, 5, 'F');
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15); pdf.setTextColor(...MARCA);
  pdf.text('Detalle del cierre', M, y + 8); y += 18;
  const datos = [['Sucursal', detalle.sucursal], ['Fecha', formatDate(c.fecha)], ['Turno', nombreTurno(c.turnoId)],
    ['Cajero', c.username], ['Estado', `${c.estado} · ${textoResultado(v.resultado)}`], ['Generado', formatDateTime(generado)]];
  tabla({ theme: 'plain', styles: { font: 'helvetica', fontSize: 9, cellPadding: { top: 2, bottom: 2, left: 0, right: 8 }, textColor: [31, 41, 51] },
    body: [datos.slice(0, 3), datos.slice(3)].map((fila) => fila.flatMap(([k, val]) => [
      { content: k, styles: { textColor: GRIS, fontStyle: 'bold' } }, txt(val || '—')])) });
  y += 4;

  // 2. Ventas: reportado por el cajero vs sistema (mismas filas y textos que la pantalla).
  titulo('Ventas: reportado por el cajero vs sistema', 200);
  const fila = (concepto, cajero, sistema, estilos) => [{ content: txt(concepto), styles: estilos }, num(cajero, estilos), num(sistema, estilos)];
  tabla({ head: [['Concepto', num('Cajero'), num('Sistema')]], columnStyles: { 1: { cellWidth: 120 }, 2: { cellWidth: 150 } },
    body: [
      fila('Apertura', '—', toMoney(v.apertura)),
      fila('Ventas en efectivo del sistema (POS)', '—', dinero(v.ventasEfectivoSistema)),
      v.cobros > 0 && fila('+ Cobros en efectivo a otras taquerías', '—', toMoney(v.cobros)),
      v.efectivoContado != null && fila('Efectivo contado en caja', toMoney(v.efectivoContado), '—'),
      fila('+ Compras, gastos y pagos en efectivo', toMoney(v.salidas), '—'),
      v.ventasIndependientes && fila('Ventas en efectivo reportadas (formato anterior)', toMoney(v.ventasEfectivoReportadas), '—'),
      v.conteoHistorico && [{ content: 'Este cierre fue registrado con una versión anterior del sistema.', colSpan: 3, styles: { textColor: GRIS, fontStyle: 'italic' } }],
      fila('Subtotal efectivo', v.subtotalCajero == null ? '—' : toMoney(v.subtotalCajero), v.subtotalSistema == null ? 'Pendiente' : toMoney(v.subtotalSistema), RESALTE),
      [{ content: 'Resultado', colSpan: 2, styles: RESALTE }, num(v.resultadoTexto, RESALTE)],
      v.faltantePagado > 0 && fila('Faltante pagado', toMoney(v.faltantePagado), '—'),
    ].filter(Boolean) });
  const fondo = notaFondo(c, detalle.siguienteApertura);
  if (fondo) nota(fondo.texto, fondo.alerta ? [180, 35, 24] : GRIS);
  tabla({ head: [['Medio', num('Cajero'), num('Sistema'), num('Diferencia'), num('Comisión'), num('Neto a recibir')]],
    body: v.medios.length ? v.medios.map((m) => [m.label, num(toMoney(m.cajero)), num(dinero(m.sistema)), num(conSigno(m.diferencia)),
      num(m.pct == null ? 'Pendiente' : `-${toMoney(m.comision)} (${m.pct}%)`), num(m.neto == null ? 'Pendiente' : toMoney(m.neto), NEGRITA)])
      : [[{ content: 'Sin ventas con Tarjeta ni PedidosYa.', colSpan: 6, styles: { textColor: GRIS, fontStyle: 'italic' } }]],
    foot: [['Total ventas del sistema', num('—'), num(dinero(v.ventasTotales)), { content: '', colSpan: 3 }]] });
  if (v.netoSobre) nota(`Neto calculado sobre ${v.netoSobre}. Compáralo con el depósito que hace el procesador de tarjetas o PedidosYa.`);
  y += 6;

  // 3. Compras: cada compra (encabezado + productos + total) se mantiene junta si cabe en una página.
  titulo(`Compras (${compras.length})`);
  if (!compras.length) nota('Sin compras.');
  compras.forEach((k) => {
    asegurar(Math.min(52 + (k.items.length + 2) * 18 + (k.observaciones ? 14 : 0), H - PIE - M));
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); pdf.setTextColor(...MARCA);
    pdf.text(txt(k.proveedorNombre || '—'), M, y + 8);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); pdf.setTextColor(...GRIS);
    pdf.text(txt([`Fact. ${k.documento || '—'}`, formatDate(k.fecha), k.tipo, formaPagoCompra(k)].join('  ·  ')), M, y + 20);
    y += 26;
    tabla({ head: [['Producto', num('Cantidad'), num('Precio total'), num('Unitario')]],
      columnStyles: { 1: { cellWidth: 70 }, 2: { cellWidth: 90 }, 3: { cellWidth: 80 } },
      body: k.items.map((i) => [txt(i.producto), num(cantidadItem(i)), num(toMoney(i.subtotal)), num(unitarioItem(i))]),
      foot: [[{ content: 'Total compra', colSpan: 2 }, num(toMoney(k.total)), '']] });
    if (k.observaciones) nota(`Observaciones: ${k.observaciones}`);
    y += 4;
  });

  // 4. Resumen de compras (misma clasificación que la pantalla). Se mantiene junto; aquí termina el PDF.
  if (compras.length) {
    const r = resumirCompras(compras);
    titulo('Resumen de compras', 18 + 5 * 20);
    tabla({ pageBreak: 'avoid', columnStyles: { 1: { cellWidth: 150 } },
      body: [
        ['Compras en efectivo (salida de caja)', num(toMoney(r.efectivo))],
        ['Compras a crédito (cuentas por pagar)', num(toMoney(r.credito))],
        r.otros > 0 && ['Otros pagos / fuera de caja', num(toMoney(r.otros))],
        [{ content: 'Total comprado (incluye crédito y pagos fuera de caja)', styles: RESALTE }, num(toMoney(r.total), RESALTE)],
      ].filter(Boolean) });
  }

  const total = pdf.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    pdf.setPage(i); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); pdf.setTextColor(...GRIS);
    pdf.text(txt(`Cierre ${formatDate(c.fecha)} · ${nombreTurno(c.turnoId)} · ${detalle.sucursal || ''}`), M, H - 20);
    pdf.text(`Página ${i} de ${total}`, W - M, H - 20, { align: 'right' });
  }
  return pdf;
}

export function exportCierreDetallePdf(detalle, opciones) {
  const pdf = construirCierreDetallePdf(detalle, opciones);
  pdf.save(nombreArchivoCierre({ sucursal: detalle.sucursal, fecha: detalle.cierre.fecha, turnoId: detalle.cierre.turnoId }));
}
